import type {
  Emoji,
  Image,
  Media,
  MediaUrls,
  Space,
} from '@tribeplatform/gql-client/types'
import type { IconEmojiSize } from '@tribeplatform/react-components/common/components'
import type {
  StaticField,
  View,
  ViewField,
} from '@tribeplatform/react-components/Views'
import { GridListColumns } from '@tribeplatform/react-ui-kit/Layout'
import type { TextVariant } from '@tribeplatform/react-ui-kit/Text'

const DEFAULT_SPACE_EMOJI = 'speech_balloon'

export const getColumns = (
  gallerySize: View['gallerySize'],
): GridListColumns => {
  if (!gallerySize) {
    return 3
  }

  switch (gallerySize) {
    case 'small':
      return 4
    case 'medium':
      return 3
    case 'large':
      return 2
    default:
      // Code should never reach the default case
      // eslint-disable-next-line no-case-declarations
      const exhaustiveCheck: never = gallerySize
      return exhaustiveCheck
  }
}

export const getCardWidth = (gallerySize: View['gallerySize']) => {
  switch (gallerySize) {
    case 'small':
      return 300
    case 'medium':
      return 400
    case 'large':
      return 500
    default:
      // Code should never reach the default case
      // eslint-disable-next-line no-case-declarations
      const exhaustiveCheck: never = gallerySize
      return exhaustiveCheck
  }
}

interface GetMedia {
  source: StaticField['settings']['bannerSource']
  space: Space
  size?: keyof MediaUrls
}
export const getMedia = ({ source, space, size = 'medium' }: GetMedia) => {
  switch (source) {
    case 'banner':
      return getMediaUrl(space?.banner, size) ?? ''
    case 'image':
      return getMediaUrl(space?.image, size) ?? ''
    case 'seo':
      return getMediaUrl(space?.customSeoDetail?.thumbnail, size) ?? ''
    default: {
      return ''
    }
  }
}

interface GetEmoji {
  source: StaticField['settings']['bannerSource']
  space: Space
}
export const getEmoji = ({ source, space }: GetEmoji) => {
  switch (source) {
    case 'banner':
      return getEmojiText(space?.banner)
    case 'image':
      return getEmojiText(space?.image)
    case 'seo':
      return getEmojiText(space?.customSeoDetail?.thumbnail)
    default: {
      return ''
    }
  }
}

const getMediaUrl = (media: Media, size: keyof MediaUrls) => {
  return (media as Image)?.urls?.[size] || (media as Image)?.url
}

const getEmojiText = (media: Media) => {
  return (media as Emoji)?.text ?? DEFAULT_SPACE_EMOJI
}

export const getTitleVariant = (
  titleSize: ViewField['settings']['titleSize'],
): TextVariant => {
  switch (titleSize) {
    case 'sm':
      return 'heading2xs'
    case 'md':
    default:
      return 'headingXs'
    case 'lg':
      return 'headingSm'
  }
}

export const getEmojiSize = (
  gallerySize: View['gallerySize'],
): IconEmojiSize => {
  switch (gallerySize) {
    case 'small':
      return 'lg'
    case 'medium':
      return 'xl'
    case 'large':
      return '2xl'
    default: {
      // Code should never reach the default case
      const exhaustiveCheck: never = gallerySize
      return exhaustiveCheck
    }
  }
}
